import axios from 'axios';
import { getStoredUser, storeUser } from '../utils/authStorage';
import { notifyAuthTokenRefreshed } from '../utils/authTokenSync';
import { shouldRefreshSession, isRejectedRefreshSession } from '../utils/authRecovery';

const apiBaseUrl = process.env.REACT_APP_API_ENDPOINT;
const refreshClient = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
});

let refreshRequest = null;
let redirectingToLogin = false;

export const axiosInstance = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
});

axiosInstance.interceptors.request.use(
  config => {
    const user = getStoredUser();
    if (user && user.authToken) {
      config.headers.Authorization = `Bearer ${user.authToken}`;
    }
    return config;
  },
  error => {
    return error;
  },
);

const isRefreshRequest = url => (url || '').includes('/users/refreshToken');

axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const response = error.response;
    const originalRequest = error.config;
    const storedUser = getStoredUser();

    if (
      !response
      || !originalRequest
      || (storedUser && storedUser.isExportSession)
      || originalRequest._retryAfterRefresh
      || isRefreshRequest(originalRequest.url)
      || !shouldRefreshSession(response, storedUser)
    ) {
      return Promise.reject(error);
    }

    originalRequest._retryAfterRefresh = true;

    if (!refreshRequest) {
      refreshRequest = refreshClient
        .post('/users/refreshToken', {})
        .then(refreshResponse => {
          const authToken = refreshResponse.data && refreshResponse.data.authToken;
          const user = getStoredUser();

          if (user && authToken) {
            storeUser({ ...user, authToken });
          }
          notifyAuthTokenRefreshed(authToken);

          return authToken;
        })
        .finally(() => {
          refreshRequest = null;
        });
    }

    try {
      const authToken = await refreshRequest;
      originalRequest.withCredentials = true;
      originalRequest.headers = originalRequest.headers || {};

      if (authToken) {
        originalRequest.headers.Authorization = `Bearer ${authToken}`;
      } else {
        delete originalRequest.headers.Authorization;
      }

      return axiosInstance(originalRequest);
    } catch (refreshError) {
      // A temporary network/server failure must not erase a valid session.
      if (!isRejectedRefreshSession(refreshError)) return Promise.reject(refreshError);
      storeUser(null);
      if (!redirectingToLogin && typeof window !== 'undefined') {
        redirectingToLogin = true;
        window.location.assign('/');
      }
      return Promise.reject(refreshError);
    }
  },
);
